import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  CircularProgress,
  Divider,
  ListItemButton,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Menu as MenuIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Chat as ChatIcon,
  Headphones as HeadphonesIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  PlayArrow as PlayArrowIcon,
  Hub as HubIcon,
  Radar as RadarIcon,
  FactCheck as FactCheckIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { ConversationProvider } from './contexts/ConversationContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { apiRequest } from './config/api';
import ProtectedRoute from './components/ProtectedRoute';
import LoginForm from './components/LoginForm';
import Agent from './pages/AgentSimple';
// import HomePage from './pages/HomePage';
import V3Test from './pages/V3Test';
import DocumentView from './pages/DocumentView';
import DocumentViewPro from './pages/DocumentViewPro';
import JsonDocumentViewer from './pages/JsonDocumentViewer';
import DocumentsPage from './pages/DocumentsPage';
import CustomPDFViewer from './pages/CustomPDFViewer';
import PDFAIChat from './pages/PDFAIChat';
import MobilePDFAIChat from './pages/MobilePDFAIChat';
import ReplayCenter from './pages/ReplayCenter';
// import CLSAPDFAIChat from './pages/CLSAPDFAIChat';
// import CLSAReplayCenter from './pages/CLSAReplayCenter';
import ClientManagement from './pages/ClientManagement';
import AgentCenter from './pages/AgentCenter';
import PortalMonitor from './pages/PortalMonitor';
import DataQuality from './pages/DataQuality';
import CompaniesPage from './pages/CompaniesPage';
import CompanyDetailPage from './pages/CompanyDetailPage';
import AnalystsPage from './pages/AnalystsPage';
import AnalystDetailPage from './pages/AnalystDetailPage';
import ComparePage from './pages/ComparePage';
// import MobileLayout from './components/mobile/MobileLayout';
import ErrorBoundary from './components/ErrorBoundary';
import NotFoundPage from './pages/NotFoundPage';
import CommandBar from './components/CommandBar';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1A2341',
    },
    secondary: {
      main: '#4A90E2',
    },
    background: {
      default: '#F7F9FB',
    },
  },
  typography: {
    fontFamily: 'Inter, Open Sans, Arial, sans-serif',
  },
});

// Navigation items with translation keys
const navigationItems = [
  { textKey: 'nav.agent', path: '/agent', icon: <ChatIcon /> },
  { textKey: 'nav.pdfChat', path: '/pdf-ai-chat', icon: <PictureAsPdfIcon /> },
  { textKey: 'nav.replayCenter', path: '/replay-center', icon: <HeadphonesIcon /> },
  { textKey: 'nav.documents', path: '/documents', icon: <PictureAsPdfIcon /> },
  { textKey: 'nav.companies', path: '/companies', icon: <BusinessIcon /> },
  { textKey: 'nav.analysts', path: '/analysts', icon: <PeopleIcon /> },
  { textKey: 'nav.agentCenter', path: '/agent-center', icon: <HubIcon /> },
  { textKey: 'nav.portalMonitor', path: '/portal-monitor', icon: <RadarIcon /> },
  { textKey: 'nav.dataQuality', path: '/data-quality', icon: <FactCheckIcon /> },
  { textKey: 'nav.clientManagement', path: '/client-management', icon: <PeopleIcon /> },
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const theme_use = useTheme();
  const isMobile = useMediaQuery(theme_use.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { uiLanguage, setUiLanguage, t } = useLanguage();
  const [scrapingLoading, setScrapingLoading] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error' | 'info'>('info');
  const pollingRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (scrapingLoading) {
      const poll = async () => {
        try {
          const statusRes = await apiRequest('/admin/scraper-run-status');
          if (statusRes.status === 'completed') {
            setScrapingLoading(false);
            setSnackbarMessage(`Success: ${statusRes.summary || 'Cycle completed'}`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          } else if (statusRes.status === 'failed') {
            setScrapingLoading(false);
            setSnackbarMessage(`Failed: ${statusRes.summary || 'See logs'}`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          }
        } catch (e) {
          console.error("Polling error", e);
        }
      };
      pollingRef.current = setInterval(poll, 3000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [scrapingLoading]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRunScraper = async () => {
    if (!user || user.email !== 'mosheabdo@gmail.com') return;
    
    setScrapingLoading(true);
    try {
      await apiRequest('/admin/run-scraper-test', {
        method: 'POST',
        body: JSON.stringify({ email: user.email })
      });
      setSnackbarMessage('Scraper started! Running in background...');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to run scraper:', error);
      setScrapingLoading(false);
      setSnackbarMessage('Failed to start scraper.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTitleClick = () => {
    navigate('/');
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
    handleProfileMenuClose();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ width: 250, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <List sx={{ flexGrow: 1 }}>
        {navigationItems.map((item) => (
          <ListItem
            key={item.textKey}
            component="a"
            href={item.path}
            sx={{
              backgroundColor: location.pathname === item.path ? '#E3F2FD' : 'transparent',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <ListItemIcon sx={{ color: location.pathname === item.path ? '#1A2341' : 'inherit' }}>
              {item.icon || <ChatIcon />}
            </ListItemIcon>
            <ListItemText
              primary={t(item.textKey)}
              sx={{
                fontWeight: location.pathname === item.path ? 700 : 400,
                color: location.pathname === item.path ? '#1A2341' : 'inherit'
              }}
            />
          </ListItem>
        ))}
      </List>

      {user && (
        <Box sx={{ pb: 2 }}>
          <Divider />
          <List>
            <ListItem>
              <ListItemText
                primary={user.email}
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'textSecondary',
                  noWrap: true,
                  title: user.email
                }}
              />
            </ListItem>

            {user.email === 'mosheabdo@gmail.com' && (
              <ListItemButton
                onClick={() => {
                  handleRunScraper();
                  setMobileOpen(false);
                }}
                disabled={scrapingLoading}
              >
                <ListItemIcon>
                  {scrapingLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                </ListItemIcon>
                <ListItemText primary={t('action.runScraper')} />
              </ListItemButton>
            )}

            {/* Language Toggle in Mobile Drawer */}
            <ListItemButton
              onClick={() => {
                setUiLanguage(uiLanguage === 'English' ? 'Hebrew' : 'English');
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>
                <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                  {uiLanguage === 'English' ? 'עב' : 'EN'}
                </Typography>
              </ListItemIcon>
              <ListItemText primary={uiLanguage === 'English' ? 'עברית' : 'English'} />
            </ListItemButton>

            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t('user.logout')} />
            </ListItemButton>
          </List>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" sx={{ bgcolor: '#1A2341' }}>
        <Toolbar>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              }
            }}
            onClick={handleTitleClick}
          >
            Psagot Global Equity Desk
          </Typography>
          
          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.textKey}
                  color="inherit"
                  href={item.path}
                  sx={{
                    backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                    fontWeight: location.pathname === item.path ? 700 : 400,
                  }}
                >
                  {t(item.textKey)}
                </Button>
              ))}
              
              {user?.email === 'mosheabdo@gmail.com' && (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleRunScraper}
                  disabled={scrapingLoading}
                  startIcon={scrapingLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  sx={{ ml: 2, mr: 1, fontWeight: 'bold', boxShadow: 'none' }}
                  size="small"
                >
                  {t('action.runScraper')}
                </Button>
              )}

              {/* Language Toggle */}
              <Button
                onClick={() => setUiLanguage(uiLanguage === 'English' ? 'Hebrew' : 'English')}
                sx={{
                  minWidth: 45,
                  color: 'white',
                  fontWeight: 'bold',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  px: 1,
                  ml: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  }
                }}
                size="small"
              >
                {uiLanguage === 'English' ? 'עב' : 'EN'}
              </Button>

              {user && (
                <IconButton
                  color="inherit"
                  onClick={handleProfileMenuOpen}
                  sx={{ ml: 1 }}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
                    {user.email?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('user.logout')}
        </MenuItem>
      </Menu>

      <CommandBar />
      <Box component="main" sx={{ flexGrow: 1, width: '100%', maxWidth: '100%' }}>
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function AppRoutes() {
  return (
    <Router>
      <Layout>
        <Routes>
          <Route path="/login" element={<LoginFormWithRedirect />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Agent />
            </ProtectedRoute>
          } />
          <Route path="/agent" element={
            <ProtectedRoute>
              <Agent />
            </ProtectedRoute>
          } />
          <Route path="/v3" element={
            <ProtectedRoute>
              <V3Test />
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute>
              <DocumentsPage />
            </ProtectedRoute>
          } />
          <Route path="/document" element={
            <ProtectedRoute>
              <DocumentView />
            </ProtectedRoute>
          } />
          <Route path="/document-pro" element={
            <ProtectedRoute>
              <DocumentViewPro />
            </ProtectedRoute>
          } />
          <Route path="/document-json" element={
            <ProtectedRoute>
              <JsonDocumentViewer />
            </ProtectedRoute>
          } />
          <Route path="/viewer" element={
            <ProtectedRoute>
              <CustomPDFViewer />
            </ProtectedRoute>
          } />
          <Route path="/pdf-ai-chat" element={
            <ProtectedRoute>
              <PDFAIChat />
            </ProtectedRoute>
          } />
          <Route path="/pdf-ai-chat/mobile" element={
            <ProtectedRoute>
              <MobilePDFAIChat />
            </ProtectedRoute>
          } />
          <Route path="/replay-center" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <ReplayCenter />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          {/* <Route path="/clsa-pdf-chat" element={
            <ProtectedRoute>
              <CLSAPDFAIChat />
            </ProtectedRoute>
          } />
          <Route path="/clsa-replay" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <CLSAReplayCenter />
              </ErrorBoundary>
            </ProtectedRoute>
          } /> */}
          <Route path="/client-management" element={
            <ProtectedRoute>
              <ClientManagement />
            </ProtectedRoute>
          } />
          <Route path="/agent-center" element={
            <ProtectedRoute>
              <AgentCenter />
            </ProtectedRoute>
          } />
          <Route path="/portal-monitor" element={
            <ProtectedRoute>
              <PortalMonitor />
            </ProtectedRoute>
          } />
          <Route path="/data-quality" element={
            <ProtectedRoute>
              <DataQuality />
            </ProtectedRoute>
          } />
          <Route path="/companies" element={
            <ProtectedRoute>
              <CompaniesPage />
            </ProtectedRoute>
          } />
          <Route path="/company/:ticker" element={
            <ProtectedRoute>
              <CompanyDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/analysts" element={
            <ProtectedRoute>
              <AnalystsPage />
            </ProtectedRoute>
          } />
          <Route path="/analyst/:id" element={
            <ProtectedRoute>
              <AnalystDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/compare" element={
            <ProtectedRoute>
              <ComparePage />
            </ProtectedRoute>
          } />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Layout>
    </Router>
  );
}

function LoginFormWithRedirect() {
  const navigate = useNavigate();
  return <LoginForm onSuccess={() => navigate('/')} />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <AuthProvider>
          <ChatProvider>
            <ConversationProvider>
              <AppRoutes />
            </ConversationProvider>
          </ChatProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;